import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useNavigate } from "react-router-dom";
import {
  Datagrid,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useDataProvider,
  useNotify,
  useRefresh,
} from "react-admin";
import type { AgentItem, AgentSession } from "../../models/AgentSession";
import type { AppDataProvider } from "../../types/dataProvider";
import { formatDate } from "../../utils/common";

/**
 * 格式化时间展示。
 */
const formatDateValue = (value: unknown): string => {
  if (typeof value !== "string" || !value) {
    return "-";
  }
  return formatDate(value);
};

/**
 * 按当前时间生成默认会话标题。
 */
const buildDefaultSessionTitle = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

/**
 * 会话状态 Chip 颜色。
 */
const getSessionStatusColor = (
  status: string
): "default" | "success" | "warning" | "error" | "info" => {
  const normalized = status.toLowerCase();
  if (normalized === "idle" || normalized === "completed" || normalized === "done") {
    return "success";
  }
  if (normalized === "running" || normalized === "processing" || normalized === "pending") {
    return "info";
  }
  if (normalized === "terminated" || normalized === "failed" || normalized === "error") {
    return "error";
  }
  if (normalized === "archived") {
    return "warning";
  }
  return "default";
};

/**
 * 单个智能体可选卡片。
 */
const AgentOptionCard: React.FC<{
  agent: AgentItem;
  selected: boolean;
  onSelect: () => void;
}> = ({ agent, selected, onSelect }) => {
  const [showSystem, setShowSystem] = React.useState(false);
  const disabled = Boolean(agent.error);

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={() => {
        if (!disabled) {
          onSelect();
        }
      }}
      onKeyDown={(event) => {
        if (disabled) {
          return;
        }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      sx={{
        border: "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? "primary.50" : disabled ? "action.disabledBackground" : "background.paper",
        borderRadius: 1.5,
        p: 1.5,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.7 : 1,
        outline: selected ? "2px solid" : "none",
        outlineColor: "primary.main",
        outlineOffset: -1,
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": disabled
          ? undefined
          : {
              borderColor: "primary.light",
              bgcolor: selected ? "primary.50" : "grey.50",
            },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: selected ? "primary.main" : "grey.200",
            color: selected ? "primary.contrastText" : "text.secondary",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {selected ? <CheckCircleIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {agent.name || agent.id}
            </Typography>
            {agent.modelId ? <Chip size="small" label={agent.modelId} variant="outlined" /> : null}
            {agent.version ? (
              <Chip size="small" label={`v${agent.version}`} variant="outlined" color="default" />
            ) : null}
          </Stack>
          {agent.description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {agent.description}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", wordBreak: "break-all" }}>
            {agent.id}
          </Typography>
          {agent.error ? (
            <Alert severity="error" sx={{ mt: 1, py: 0 }}>
              {agent.error}
            </Alert>
          ) : null}
          {agent.system ? (
            <Box sx={{ mt: 0.75 }}>
              <Button
                size="small"
                color="inherit"
                endIcon={showSystem ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowSystem((previous) => !previous);
                }}
                sx={{ px: 0.5, minWidth: 0, textTransform: "none" }}
              >
                {showSystem ? "收起系统提示" : "查看系统提示"}
              </Button>
              <Collapse in={showSystem}>
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 1,
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    maxHeight: 180,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: 12,
                    m: 0,
                  }}
                >
                  {agent.system}
                </Box>
              </Collapse>
            </Box>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
};

/**
 * 新建会话对话框。
 */
const CreateSessionDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: (sessionId: string) => void;
}> = ({ open, onClose, onCreated }) => {
  const dataProvider = useDataProvider() as AppDataProvider;
  const notify = useNotify();
  const [agents, setAgents] = React.useState<AgentItem[]>([]);
  const [loadingAgents, setLoadingAgents] = React.useState(false);
  const [loadError, setLoadError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [agentId, setAgentId] = React.useState("");
  const [title, setTitle] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const loadAgents = async () => {
      try {
        setLoadingAgents(true);
        setLoadError("");
        const response = await dataProvider.getAgentAgents();
        if (cancelled) {
          return;
        }
        const list = Array.isArray(response.data) ? response.data : [];
        setAgents(list);
        const firstAvailable = list.find((item) => !item.error);
        if (firstAvailable) {
          setAgentId((previous) => previous || firstAvailable.id);
        }
      } catch (error) {
        if (!cancelled) {
          setAgents([]);
          setLoadError("加载智能体列表失败");
          notify("加载智能体列表失败", { type: "error" });
        }
      } finally {
        if (!cancelled) {
          setLoadingAgents(false);
        }
      }
    };
    void loadAgents();
    return () => {
      cancelled = true;
    };
  }, [open, dataProvider, notify]);

  React.useEffect(() => {
    if (!open) {
      setTitle("");
      setSubmitting(false);
      setLoadError("");
      setAgentId("");
    }
  }, [open]);

  const selectedAgent = agents.find((item) => item.id === agentId);

  const handleCreate = async () => {
    if (!agentId) {
      notify("请选择智能体", { type: "warning" });
      return;
    }
    if (selectedAgent?.error) {
      notify("所选智能体不可用", { type: "warning" });
      return;
    }
    try {
      setSubmitting(true);
      const response = await dataProvider.createAgentSession({
        agent: agentId,
        title: title.trim() || buildDefaultSessionTitle(),
      });
      const apiSessionId = response.data?.sessionId || response.data?.id;
      if (!apiSessionId) {
        notify("创建成功但未返回会话 ID", { type: "warning" });
        onClose();
        return;
      }
      notify("会话已创建", { type: "success" });
      onCreated(apiSessionId);
    } catch (error) {
      notify("创建会话失败", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>新建任务会话</DialogTitle>
      <DialogContent dividers>
        {loadingAgents ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {loadError ? <Alert severity="error">{loadError}</Alert> : null}
            {!loadError && agents.length === 0 ? (
              <Alert severity="info">暂无可用智能体</Alert>
            ) : null}
            {agents.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  选择智能体
                </Typography>
                <Stack spacing={1.25}>
                  {agents.map((agent) => (
                    <AgentOptionCard
                      key={agent.id}
                      agent={agent}
                      selected={agentId === agent.id}
                      onSelect={() => setAgentId(agent.id)}
                    />
                  ))}
                </Stack>
              </Box>
            ) : null}
            <MuiTextField
              size="small"
              label="会话名称"
              placeholder="不填则按当前时间自动生成"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          取消
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleCreate()}
          disabled={submitting || loadingAgents || !agentId || Boolean(selectedAgent?.error)}
        >
          {submitting ? "创建中..." : "创建"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * 任务会话列表操作栏。
 */
const ListActions: React.FC<{ onCreate: () => void }> = ({ onCreate }) => {
  return (
    <TopToolbar sx={{ width: "100%", alignItems: "center", gap: 1, flexWrap: "wrap", px: 2, py: 1 }}>
      <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
        新建会话
      </Button>
    </TopToolbar>
  );
};

/**
 * 任务会话表格。
 */
const AgentSessionsDatagrid: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Datagrid
      rowClick={(_id, _basePath, record) => {
        const session = record as AgentSession;
        const apiSessionId = session?.sessionId || String(_id);
        navigate(`/agent/sessions/${encodeURIComponent(apiSessionId)}`);
        return false as const;
      }}
      bulkActionButtons={false}
    >
      <TextField source="sessionId" label="会话 ID" />
      <TextField source="title" label="标题" />
      <FunctionField
        label="智能体"
        render={(record: AgentSession) => record?.agentName || record?.agentId || "-"}
      />
      <FunctionField
        label="状态"
        sortBy="status"
        render={(record: AgentSession) => {
          const status = record?.status || "-";
          return <Chip size="small" label={status} color={getSessionStatusColor(status)} />;
        }}
      />
      <FunctionField
        label="创建时间"
        sortBy="createdAt"
        render={(record: AgentSession) => formatDateValue(record?.createdAt)}
      />
      <FunctionField
        label="更新时间"
        sortBy="updatedAt"
        render={(record: AgentSession) => formatDateValue(record?.updatedAt)}
      />
    </Datagrid>
  );
};

/**
 * 智能体任务会话列表页。
 */
const AgentSessionsPage: React.FC = (props) => {
  const navigate = useNavigate();
  const refresh = useRefresh();
  const [createOpen, setCreateOpen] = React.useState(false);

  return (
    <>
      <List
        {...props}
        title="任务会话"
        actions={<ListActions onCreate={() => setCreateOpen(true)} />}
        sort={{ field: "createdAt", order: "DESC" }}
        perPage={25}
      >
        <AgentSessionsDatagrid />
      </List>
      <CreateSessionDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(sessionId) => {
          setCreateOpen(false);
          refresh();
          navigate(`/agent/sessions/${sessionId}`);
        }}
      />
    </>
  );
};

export default AgentSessionsPage;
